import { CustomPaging, GridColumnExtension, PagingState } from "@devexpress/dx-react-grid";
import {
  ColumnChooser,
  Grid,
  PagingPanel,
  TableColumnVisibility,
  TableFixedColumns,
  TableHeaderRow,
  Toolbar,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, Button, Card } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { FormattedMessage, useIntl } from "react-intl";
import { SizeMe } from "react-sizeme";
import SweetAlert from "sweetalert2";
import { useLocaleProvider } from "../../../../components/locale-provider";
import { SectionHeading, SectionSubHeading } from "../../../../components/section-heading";
import "@devexpress/dx-react-grid-bootstrap4/dist/dx-react-grid-bootstrap4.css";
import { Identifiable } from "../../context";
import {
  Chevron,
  ColumnsMessage,
  CrudListContainer,
  CustomCardTitle,
  ExportButton,
  Loader,
  LoadError,
  NoData,
  StyledCard,
  StyledCardDeck,
  StyledReactTooltip,
  StyledSimpleRow,
  StyledTableBody,
  StyledTableRow,
  TitleChevronContainer,
} from "./components";
import { defaultListMessages } from "./CrudList.messages";
import { CrudListUncontrolledProps, isTableConfig } from "./types";
import { generateCSVData, generateCSVFilename } from "./utils/csvExport";

const getRowId = (row: any) => {
  return row.id;
};

const EMPTY_FILTER_VALUES = {};

/**
 * CrudListUncontrolled - A presentation component for CRUD list operations.
 *
 * This component is "uncontrolled" meaning it does NOT manage data fetching,
 * pagination state, or filter state. The parent component is responsible for:
 * - Fetching data (e.g., using @tanstack/react-query)
 * - Managing pagination state (currentPage, pageSize, totalCount)
 * - Managing filter values
 * - Handling data transformations
 *
 * The component DOES manage internally:
 * - Column visibility (persisted to localStorage)
 * - Filter accordion open/closed state
 * - Reordering UI state (loading spinner)
 * - Date picker focus state
 *
 * @example
 * ```typescript
 * function MyListPage() {
 *   const [currentPage, setCurrentPage] = useState(0);
 *   const [pageSize, setPageSize] = useState(50);
 *   const [filterValues, setFilterValues] = useState({});
 *
 *   const { data, isLoading, error } = useQuery({
 *     queryKey: ['items', currentPage, pageSize, filterValues],
 *     queryFn: () => fetchItems({ currentPage, pageSize, ...filterValues }),
 *   });
 *
 *   return (
 *     <CrudListUncontrolled
 *       items={data?.items || []}
 *       isLoading={isLoading}
 *       error={error}
 *       pagination={{
 *         currentPage,
 *         pageSize,
 *         totalCount: data?.totalCount || 0,
 *         onPageChange: setCurrentPage,
 *         onPageSizeChange: (size) => {
 *           setPageSize(size);
 *           setCurrentPage(0);
 *         },
 *         pageSizes: [50, 100, 500],
 *       }}
 *       filterValues={filterValues}
 *       onFilterChange={(newFilters) => {
 *         setFilterValues(newFilters);
 *         setCurrentPage(0);
 *       }}
 *       columns={columns}
 *       filters={tableFilters}
 *     />
 *   );
 * }
 * ```
 */
export function CrudListUncontrolled<T extends Identifiable, F = {}>({
  items,
  isLoading,
  error,
  pagination,
  hasPagination = true,
  filterValues = EMPTY_FILTER_VALUES,
  onFilterChange,
  filters,
  filtersClosedByDefault = true,
  columns,
  messages = defaultListMessages,
  subHeader,
  filterBar,
  actions,
  rowActions,
  actionsWidth = 290,
  rowActionsTooltip,
  fixedActions,
  onReorder,
  onExport,
  exportLoading = false,
  onExportText,
  hideExportButton,
  CSVRenderers,
  managerName,
  columnVisibility,
  columnVisibilityExtensions = [],
  initialHiddenColumnNames,
  listHeight,
  additionalHeaderContent,
  additionalFooterContent,
  customRow,
}: CrudListUncontrolledProps<T, F>): JSX.Element {
  const intl = useIntl();
  const locale = useLocaleProvider();

  // UI-only state (kept internal)
  const [reordering, setReordering] = useState(false);
  const [toggleClosed, setToggleClosed] = useState(filtersClosedByDefault);
  const [hiddenColumnNames, setHiddenColumnNames] = useState<string[]>(() =>
    window.localStorage.getItem("hiddenColumnNames")
      ? JSON.parse(window.localStorage.getItem("hiddenColumnNames") as string)
      : initialHiddenColumnNames || []
  );
  const [datePickerInFocus, setDatePickerInFocus] = useState();
  // Local filter state - stores filter changes before applying them
  const [localFilterValues, setLocalFilterValues] = useState<Partial<F>>(filterValues || {});

  // Persist column visibility to localStorage
  useEffect(() => {
    if (hiddenColumnNames) {
      window.localStorage.setItem("hiddenColumnNames", JSON.stringify(hiddenColumnNames));
    }
  }, [hiddenColumnNames]);

  // Sync local filter values with parent when filterValues prop changes
  useEffect(() => {
    setLocalFilterValues(filterValues || {});
  }, [filterValues]);

  const columnExtensions = useMemo(
    () =>
      Object.keys(columns)
        .reduce((r, k) => {
          const config = columns[k as keyof T];
          if (isTableConfig(config) && config.children) {
            const children = Object.keys(config.children).map(column => {
              const width = (config.children && config.children[column].width) || undefined;
              const align = (config.children && config.children[column].align) || undefined;
              return {
                columnName: `${k}.${column}`,
                width,
                align,
              };
            });
            r = [...r, ...children];
          } else {
            const width = isTableConfig(config) ? config.width : undefined;
            const align = isTableConfig(config) ? config.align : undefined;
            r.push({
              columnName: k,
              width,
              align,
            });
          }
          return r;
        }, [] as Array<GridColumnExtension>)
        .concat({ columnName: "__actions", width: actionsWidth, align: "left" }),
    [actionsWidth, columns]
  );

  const NoDataCell = useMemo(() => {
    return () =>
      isLoading ? (
        <Loader loadMessage={messages.loading} />
      ) : error ? (
        <LoadError failMessage={messages.loadError} retryMessage={messages.loadRetry} />
      ) : (
        <NoData noDataMessage={messages.noData} />
      );
  }, [isLoading, error, messages]);

  const CellRenderer = useMemo(
    () => (props: any) => {
      const columnWidth =
        props.tableColumn.width ||
        document.getElementById(`column-id-${props.column.name}`)?.offsetWidth;

      const columnAlign = props.tableColumn.align;

      const rowType =
        Array.isArray(items) && items.findIndex(it => it.id === props.row.id) % 2 === 0
          ? "odd"
          : "even";

      if (props.column.name === "__actions") {
        return (
          <>
            {rowActions &&
              rowActions({
                rows: items,
                row: items ? items.find(row => row.id === props.row.id) : props.row,
                onReloadList: () => {
                  // Note: In uncontrolled version, parent should refetch via useQuery
                },
                rest: {
                  ...props,
                  className: `${props.className} ${rowType}`,
                },
              })}
          </>
        );
      }
      const [field, childField] = props.column.name.split(".");
      const config = columns[field as keyof T] as any;
      const button = (config as any).button;
      let R = config && isTableConfig(config) ? config.renderer : config;
      if (isTableConfig(config) && childField && config.children) {
        R = config.children[childField].renderer;
      }
      if (!R) {
        return <td />;
      }
      return (
        <td style={{ width: columnWidth, textAlign: columnAlign }}>
          <R value={props.value} button={button} rows={props.row} />
        </td>
      );
    },
    [rowActions, columns, items]
  );

  const tableColumns = useMemo(() => {
    const objectColumns = Object.keys(columns).reduce((results, field) => {
      const column = columns[field as keyof T];
      if (isTableConfig(column) && column.children) {
        const children = Object.keys(column.children).map(childField => ({
          name: `${field}.${childField}`,
          title: messages?.tableHeaders?.[childField]
            ? intl.formatMessage(messages?.tableHeaders[childField])
            : childField,
        }));
        results = [...results, ...children];
      } else {
        results.push({
          name: field,
          title: messages?.tableHeaders?.[field]
            ? intl.formatMessage(messages?.tableHeaders[field])
            : field,
        });
      }
      return results;
    }, [] as Array<any>);

    if (rowActions) {
      objectColumns.push({
        name: "__actions",
        title: intl.formatMessage(messages?.tableActions),
      });
    }
    return objectColumns;
  }, [columns, intl, rowActions, messages]);

  const rowData = useMemo(() => {
    if (!items) {
      return [];
    }
    return items.map(d => {
      return tableColumns.reduce(
        (r, { name: columnName }) => {
          const [field] = columnName.split(".");
          return {
            ...r,
            [columnName]: d[field as keyof T],
          };
        },
        { ...d }
      );
    });
  }, [items, tableColumns]);

  const rows = useMemo(() => rowData || [], [rowData]);

  const handleReorder = useCallback(
    async ({ oldIndex, newIndex }) => {
      // Don't call while a call is in progress
      if (reordering) return;

      if (onReorder) {
        setReordering(true);
        if (oldIndex <= newIndex) {
          newIndex = newIndex + 1;
        }
        const entityId = rows[oldIndex].id;
        const afterId = newIndex > 0 ? rows[newIndex - 1].id : undefined;
        try {
          await onReorder(entityId, afterId);
          // Success - parent should refetch to get the updated order
        } catch (e) {
          console.log("Error while reordering", e);
          // Revert optimistic update on error
          await SweetAlert.fire({
            icon: "error",
            title: intl.formatMessage(messages?.reorderFailedTitle),
            text: intl.formatMessage(messages?.reorderFailedContent),
          });
        } finally {
          setReordering(false);
        }
      }
    },
    [rows, onReorder, setReordering, reordering, intl, messages]
  );

  const Body = useMemo(
    () => ({ row, ...restProps }: any) => {
      return <StyledTableBody {...restProps} onSortEnd={handleReorder} useDragHandle />;
    },
    [handleReorder]
  );

  const Row = useMemo(
    () => ({ row, ...restProps }: any) => {
      return <StyledTableRow {...restProps} index={rows.indexOf(row)} />;
    },
    [rows]
  );

  const rowActionsTooltipNode = useMemo(() => rowActionsTooltip && rowActionsTooltip(), [
    rowActionsTooltip,
  ]);

  const HeaderRowContent = useMemo(
    () => ({ column, children, ...restProps }: any) => (
      <TableHeaderRow.Content
        {...restProps}
        id={`column-id-${column.name}`}
        data-tip="React-tooltip"
        data-for={`tooltip-${column.name}`}>
        {messages?.tableHeaders && messages?.tableHeaders[column.name] && (
          <StyledReactTooltip id={`tooltip-${column.name}`} effect="solid">
            <FormattedMessage
              {...(messages?.tooltipHeaders && messages?.tooltipHeaders[column.name]
                ? messages?.tooltipHeaders[column.name]
                : messages?.tableHeaders[column.name])}
            />
          </StyledReactTooltip>
        )}
        {column.name === "__actions" && rowActionsTooltipNode}
        {children}
      </TableHeaderRow.Content>
    ),
    [messages, rowActionsTooltipNode]
  );

  const handleFilterChange = useCallback((key: keyof F, newValue: any) => {
    setLocalFilterValues(prev => ({ ...prev, [key]: newValue }));
  }, []);

  const handleOnFilter = useCallback(() => {
    if (!onFilterChange) return;
    onFilterChange(localFilterValues as Partial<F>);
  }, [localFilterValues, onFilterChange]);

  const handleClearFilters = useCallback(() => {
    if (!onFilterChange) return;
    setLocalFilterValues({});
    onFilterChange({});
  }, [onFilterChange]);

  const handleFocusChange = useCallback((inFocus: any) => {
    setDatePickerInFocus(inFocus);
  }, []);

  const handleToggleClick = useCallback(() => {
    setToggleClosed(!toggleClosed);
  }, [toggleClosed]);

  const toggleButtonComponent = useCallback(
    (props: any) => (
      <ColumnsMessage onClick={props.onToggle} ref={props.buttonRef}>
        <FormattedMessage {...messages.columns} />
        <Chevron toggleClosed={true} />
      </ColumnsMessage>
    ),
    [messages]
  );

  const customRowsMemo = useMemo(() => {
    if (typeof customRow === "function" && rows) {
      return customRow(rows);
    }
  }, [customRow, rows]);

  const CSVReadyData = useMemo(
    () =>
      generateCSVData({
        items,
        columns,
        hiddenColumnNames,
        messages,
        intl,
        CSVRenderers,
        locale: locale?.currentLocale,
      }),
    [items, columns, hiddenColumnNames, messages, intl, CSVRenderers, locale]
  );

  const handleExport = useCallback(async () => {
    if (!onExport) return;
    await onExport();
  }, [onExport]);

  const addBottomMarginToColumnOverlay = useMemo(
    () => (columns ? Object.keys(columns).length > 20 : false),
    [columns]
  );

  return (
    <CrudListContainer
      odd={rows.length % 2 === 0}
      addBottomMarginToColumnOverlay={addBottomMarginToColumnOverlay}
      resultsMsg={intl.formatMessage(messages?.resultPerPage)}>
      <SectionHeading>
        <FormattedMessage {...messages.entityName} />
      </SectionHeading>
      {subHeader && <SectionSubHeading>{subHeader}</SectionSubHeading>}
      {filterBar}

      {filters && (
        <Accordion defaultActiveKey={filtersClosedByDefault ? undefined : "0"}>
          <Card>
            <Card.Body>
              <Accordion.Toggle
                as={CustomCardTitle}
                variant="link"
                eventKey="0"
                onClick={handleToggleClick}>
                <TitleChevronContainer>
                  <span>
                    <FormattedMessage {...messages.filtersTitle} />
                  </span>
                  <Chevron toggleClosed={toggleClosed} />
                </TitleChevronContainer>
              </Accordion.Toggle>
              <Accordion.Collapse eventKey="0" className="!visible">
                <>
                  <StyledCardDeck>
                    {Object.keys(filters).map(k => {
                      const key = k as keyof F & keyof T & string;
                      const filter = filters[key] as any;
                      const Widget: React.ComponentType<any> = filter.widget;
                      return (
                        <StyledSimpleRow key={key}>
                          <StyledCard>
                            <Widget
                              value={localFilterValues[key] || ""}
                              onChange={(newValue: any) => handleFilterChange(key, newValue)}
                              placeholder={
                                (filter.title && intl.formatMessage(filter.title)) ||
                                tableColumns.find(n => n.name === key)?.title ||
                                ""
                              }
                              onFocusChange={handleFocusChange}
                              focused={datePickerInFocus}
                              width={filter.width}
                            />
                          </StyledCard>
                        </StyledSimpleRow>
                      );
                    })}
                  </StyledCardDeck>
                  <div className="flex gap-2">
                    <Button variant="primary" onClick={handleOnFilter}>
                      <FormattedMessage {...messages.filter} />
                    </Button>
                    <Button variant="primary" onClick={handleClearFilters}>
                      <FormattedMessage {...messages.clearFilters} />
                    </Button>
                  </div>
                </>
              </Accordion.Collapse>
            </Card.Body>
          </Card>
        </Accordion>
      )}

      <div style={{ marginBottom: 20 }} />
      <div
        className="ml-3"
        style={{
          marginBottom: "15px",
          display: "flex",
          justifyContent: "space-between",
        }}>
        <span style={{ display: "flex" }}>
          {actions && <div>{actions()}</div>}
          {!hideExportButton &&
            (onExport ? (
              <ExportButton disabled={exportLoading} onClick={handleExport} variant="primary">
                {exportLoading ? (
                  <FormattedMessage {...messages?.loading} />
                ) : onExportText ? (
                  onExportText
                ) : (
                  <FormattedMessage {...messages.export} />
                )}
              </ExportButton>
            ) : (
              <CSVLink
                style={{ marginLeft: "10px", marginRight: "18px" }}
                data={CSVReadyData}
                filename={generateCSVFilename(managerName)}>
                <Button variant="primary">
                  <FormattedMessage {...messages.export} />
                </Button>
              </CSVLink>
            ))}

          {reordering && (
            <div style={{ marginLeft: "auto", alignSelf: "center" }}>
              <FontAwesomeIcon icon={faCircleNotch} spin />
            </div>
          )}
        </span>
        {additionalHeaderContent && (
          <div style={{ marginLeft: "20px", marginRight: "20px", overflow: "hidden" }}>
            {additionalHeaderContent}
          </div>
        )}
      </div>

      <SizeMe monitorHeight monitorWidth={false} noPlaceholder>
        {() => (
          <div style={{ flexGrow: 1 }} className="ml-3 mr-3">
            <Grid rows={rows} getRowId={getRowId} columns={tableColumns}>
              {/*PagingState and CustomPaging must be independent. If they're inside a Fragment (<></>) won't work*/}
              {hasPagination && pagination && (
                <PagingState
                  currentPage={pagination.currentPage}
                  pageSize={pagination.pageSize}
                  onCurrentPageChange={pagination.onPageChange}
                  onPageSizeChange={pagination.onPageSizeChange}
                />
              )}
              {hasPagination && pagination && <CustomPaging totalCount={pagination.totalCount} />}

              <VirtualTable
                columnExtensions={columnExtensions}
                height={listHeight}
                noDataCellComponent={NoDataCell}
                cellComponent={CellRenderer}
                bodyComponent={Body}
                rowComponent={customRowsMemo || Row}
                estimatedRowHeight={53}
              />
              <TableHeaderRow contentComponent={HeaderRowContent} />
              {fixedActions && <TableFixedColumns rightColumns={["__actions"]} />}
              {columnVisibility && (
                <TableColumnVisibility
                  hiddenColumnNames={hiddenColumnNames}
                  onHiddenColumnNamesChange={setHiddenColumnNames}
                  columnExtensions={[
                    { columnName: "__actions", togglingEnabled: false },
                    ...columnVisibilityExtensions,
                  ]}
                />
              )}
              {columnVisibility && <Toolbar />}
              {columnVisibility && <ColumnChooser toggleButtonComponent={toggleButtonComponent} />}

              {hasPagination && pagination && <PagingPanel pageSizes={pagination.pageSizes} />}
            </Grid>
            {additionalFooterContent}
          </div>
        )}
      </SizeMe>
    </CrudListContainer>
  );
}
